type MetadataRecord = Record<string, unknown>;

export type BranchGuestActionMetadata = {
  website?: string;
  mapsUrl?: string;
  whatsapp?: string;
  reservationUrl?: string;
  orderUrl?: string;
  orderLabel?: string;
  deliveryUrl?: string;
  reviewUrl?: string;
  paymentLink?: string;
};

export type ResolvedBranchGuestActions = BranchGuestActionMetadata & {
  phone?: string;
  directionsTarget?: string;
};

export type ResolvedPublicGuestAction = {
  key: 'call' | 'directions' | 'website' | 'whatsapp' | 'reservation' | 'order' | 'delivery' | 'review' | 'payment';
  label: string;
  href: string;
  external?: boolean;
};

function asMetadataRecord(input: unknown): MetadataRecord {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as MetadataRecord)
    : {};
}

function readString(record: MetadataRecord, key: string) {
  const value = record[key];
  return typeof value === 'string' && value.trim().length > 0
    ? value.trim()
    : undefined;
}

function normalizeExternalUrl(value?: string) {
  if (!value) return null;
  if (/^[a-z][a-z0-9+.-]*:/i.test(value)) {
    if (/^javascript:/i.test(value)) return null;
    if (/^data:/i.test(value)) return null;
    return value;
  }
  return `https://${value}`;
}

function buildMapsUrl(candidate?: string) {
  if (!candidate) return null;
  if (/^https?:\/\//i.test(candidate)) {
    return candidate;
  }
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(candidate)}`;
}

function buildWhatsAppUrl(rawValue?: string) {
  if (!rawValue) return null;
  if (/^https?:\/\//i.test(rawValue)) {
    return rawValue;
  }

  const normalizedNumber = rawValue.replace(/[^\d+]/g, '').replace(/^\+/, '');
  if (!normalizedNumber) return null;
  return `https://wa.me/${normalizedNumber}`;
}

export function readBranchGuestActionMetadata(input: unknown): BranchGuestActionMetadata {
  const metadata = asMetadataRecord(input);

  return {
    website: readString(metadata, 'website'),
    mapsUrl: readString(metadata, 'maps_url') || readString(metadata, 'google_maps_url'),
    whatsapp: readString(metadata, 'whatsapp') || readString(metadata, 'whatsapp_url') || readString(metadata, 'whatsapp_link'),
    reservationUrl: readString(metadata, 'reservation_url') || readString(metadata, 'booking_url'),
    orderUrl: readString(metadata, 'order_url') || readString(metadata, 'online_order_url') || readString(metadata, 'order_link'),
    orderLabel: readString(metadata, 'order_label'),
    deliveryUrl: readString(metadata, 'delivery_url'),
    reviewUrl: readString(metadata, 'review_url') || readString(metadata, 'google_review_url'),
    paymentLink: readString(metadata, 'payment_link') || readString(metadata, 'payment_url') || readString(metadata, 'checkout_url'),
  };
}

export function buildBranchGuestActionsRecord(input: BranchGuestActionMetadata) {
  const nextRecord: MetadataRecord = {
    website: input.website?.trim() || undefined,
    maps_url: input.mapsUrl?.trim() || undefined,
    whatsapp: input.whatsapp?.trim() || undefined,
    reservation_url: input.reservationUrl?.trim() || undefined,
    order_url: input.orderUrl?.trim() || undefined,
    order_label: input.orderLabel?.trim() || undefined,
    delivery_url: input.deliveryUrl?.trim() || undefined,
    review_url: input.reviewUrl?.trim() || undefined,
    payment_link: input.paymentLink?.trim() || undefined,
  };

  return Object.fromEntries(
    Object.entries(nextRecord).filter(([, value]) => value !== undefined && value !== null && value !== ''),
  );
}

export function resolveBranchGuestActions(input: {
  branch?: {
    phone?: string | null;
    address?: string | null;
    guest_actions?: unknown;
  } | null;
  business?: {
    phone?: string | null;
    address?: string | null;
    location?: string | null;
    website?: string | null;
    google_maps_url?: string | null;
  } | null;
}): ResolvedBranchGuestActions {
  const branchMetadata = readBranchGuestActionMetadata(input.branch?.guest_actions);

  return {
    phone: input.branch?.phone?.trim() || input.business?.phone?.trim() || undefined,
    directionsTarget:
      branchMetadata.mapsUrl ||
      input.branch?.address?.trim() ||
      input.business?.google_maps_url?.trim() ||
      input.business?.address?.trim() ||
      input.business?.location?.trim() ||
      undefined,
    website: branchMetadata.website || input.business?.website?.trim() || undefined,
    mapsUrl: branchMetadata.mapsUrl,
    whatsapp: branchMetadata.whatsapp,
    reservationUrl: branchMetadata.reservationUrl,
    orderUrl: branchMetadata.orderUrl,
    orderLabel: branchMetadata.orderLabel,
    deliveryUrl: branchMetadata.deliveryUrl,
    reviewUrl: branchMetadata.reviewUrl,
    paymentLink: branchMetadata.paymentLink,
  };
}

export function listConfiguredGuestActionLabels(actions: ResolvedBranchGuestActions) {
  const labels: string[] = [];

  if (actions.phone) labels.push('Call');
  if (actions.directionsTarget) labels.push('Directions');
  if (actions.website) labels.push('Website');
  if (actions.whatsapp) labels.push('WhatsApp');
  if (actions.reservationUrl) labels.push('Reserve');
  if (actions.orderUrl) labels.push(actions.orderLabel?.trim() || 'Order');
  if (actions.deliveryUrl) labels.push('Delivery');
  if (actions.reviewUrl) labels.push('Review');
  if (actions.paymentLink) labels.push('Pay');

  return labels;
}

export function buildPublicGuestActions(actions: ResolvedBranchGuestActions): ResolvedPublicGuestAction[] {
  const resolved: ResolvedPublicGuestAction[] = [];
  const seen = new Set<string>();

  const push = (action: ResolvedPublicGuestAction | null) => {
    if (!action) return;
    const key = `${action.key}:${action.href}`;
    if (seen.has(key)) return;
    seen.add(key);
    resolved.push(action);
  };

  if (actions.phone) {
    push({
      key: 'call',
      label: 'Call',
      href: `tel:${actions.phone}`,
      external: false,
    });
  }

  const directionsHref = buildMapsUrl(actions.directionsTarget);
  if (directionsHref) {
    push({
      key: 'directions',
      label: 'Directions',
      href: directionsHref,
    });
  }

  const websiteHref = normalizeExternalUrl(actions.website);
  if (websiteHref) {
    push({
      key: 'website',
      label: 'Website',
      href: websiteHref,
    });
  }

  const whatsappHref = buildWhatsAppUrl(actions.whatsapp);
  if (whatsappHref) {
    push({
      key: 'whatsapp',
      label: 'WhatsApp',
      href: whatsappHref,
    });
  }

  const reservationHref = normalizeExternalUrl(actions.reservationUrl);
  if (reservationHref) {
    push({
      key: 'reservation',
      label: 'Reserve',
      href: reservationHref,
    });
  }

  const orderHref = normalizeExternalUrl(actions.orderUrl);
  if (orderHref) {
    push({
      key: 'order',
      label: actions.orderLabel?.trim() || 'Order',
      href: orderHref,
    });
  }

  const deliveryHref = normalizeExternalUrl(actions.deliveryUrl);
  if (deliveryHref && deliveryHref !== orderHref) {
    push({
      key: 'delivery',
      label: 'Delivery',
      href: deliveryHref,
    });
  }

  const reviewHref = normalizeExternalUrl(actions.reviewUrl);
  if (reviewHref) {
    push({
      key: 'review',
      label: 'Review',
      href: reviewHref,
    });
  }

  const paymentHref = normalizeExternalUrl(actions.paymentLink);
  if (paymentHref) {
    push({
      key: 'payment',
      label: 'Pay',
      href: paymentHref,
    });
  }

  return resolved;
}
